import { FiSearch } from 'react-icons/fi';

export const listeningDetailsData = {
  courseId: 'listening-for-details',
  courseType: 'listening-course',
  title: 'Listening for Details',
  icon: FiSearch,
  overview: {
    impact: 'Critical for accuracy in gap-fill and short answers',
    focus: 'Catching specific information precisely',
    uses: 'All listening question types',
    duration: '25 minutes',
  },
  sections: [
    {
      title: 'Types of Details to Listen For',
      content: `<p>IELTS Listening requires you to catch various specific details:</p>`,
      table: {
        headers: ['Detail Type', 'Examples', 'Common Errors'],
        rows: [
          ['Numbers', '15, 50, 500', 'Confusing 15 and 50'],
          ['Dates', 'March 15th, 2024', 'Wrong day or month'],
          ['Times', '9:30 am, quarter past 10', 'AM/PM confusion'],
          ['Names', 'McPherson, O\'Brien', 'Spelling errors'],
          ['Addresses', '47 Oak Street', 'Wrong number or street'],
          ['Prices', '$15.50, £20', 'Decimal place errors'],
        ],
      },
    },
    {
      title: 'Common Listening Traps',
      content: `<p>Test makers deliberately include distractors. Watch out for:</p>`,
      points: [
        'Initial answer followed by correction: "It\'s on Tuesday... no wait, Wednesday"',
        'Multiple similar numbers mentioned: "We have 15 seats, but 50 people applied"',
        'Partial information: Only part of a name or address given initially',
        'Speaker confusion: One speaker gives wrong info, another corrects',
      ],
      warning: 'Always listen for corrections! The last piece of information is usually correct.',
    },
    {
      title: 'Spelling Awareness',
      content: `<p>Spelling counts! Be prepared for:</p>`,
      points: [
        'Unusual spellings: "Ph" as in "Stephen", double letters',
        'Homophones: hear/here, their/there/they\'re',
        'British vs American spelling: centre/center, colour/color',
        'Names that are spelled out: "That\'s B-E-A-U-M-O-N-T"',
      ],
      examples: [
        { label: 'Spelled out', text: '"My name is Smith. That\'s S-M-I-T-H."' },
        { label: 'Clarified', text: '"Thompson with a P, not an F."' },
      ],
    },
    {
      title: 'Numbers and Measurements',
      content: `<p>Pay special attention to number formats:</p>`,
      table: {
        headers: ['Type', 'What You Hear', 'What You Write'],
        rows: [
          ['Phone numbers', 'double six, oh four', '6604'],
          ['Decimals', 'fifteen point five', '15.5'],
          ['Ordinals', 'the twenty-first', '21st'],
          ['Large numbers', 'three thousand', '3,000 or 3000'],
          ['Time', 'quarter to ten', '9:45'],
        ],
      },
    },
  ],
  exercises: [
    {
      title: 'Number Recognition',
      type: 'select',
      instructions: 'Choose what you would write for each spoken phrase.',
      questions: [
        {
          text: 'You hear: "The conference is on the twenty-third of November."',
          options: ['23 November', 'November 23rd', 'Both are acceptable'],
          answer: 'Both are acceptable',
          explanation: 'Both date formats are accepted in IELTS.',
        },
        {
          text: 'You hear: "That\'s double five, three, eight, oh."',
          options: ['55380', '5538O', '553-80'],
          answer: '55380',
          explanation: '"Double five" = 55, "oh" = 0.',
        },
        {
          text: 'You hear: "It costs fifteen pounds fifty."',
          options: ['£15.50', '£1550', '15.50 pounds'],
          answer: '£15.50',
          explanation: 'Use proper currency format with decimal.',
        },
        {
          text: 'You hear: "We need approximately two thousand participants."',
          options: ['2000 / 2,000', 'two thousand', 'approx. 2000'],
          answer: '2000 / 2,000',
          explanation: 'Write numbers as digits unless asked otherwise.',
        },
        {
          text: 'You hear: "That\'s at quarter past nine."',
          options: ['9:15', '9:45', '15 past 9'],
          answer: '9:15',
          explanation: '"Quarter past" = 15 minutes after the hour.',
        },
        {
          text: 'You hear: "It\'s on the third of May, 2023."',
          options: ['3 May 2023', 'May 3rd 2023', 'Both are acceptable'],
          answer: 'Both are acceptable',
          explanation: 'Both date formats work in IELTS.',
        },
      ],
    },
    {
      title: 'Identify the Correction',
      type: 'select',
      instructions: 'What is the CORRECT answer after the speaker\'s correction?',
      questions: [
        {
          text: 'Speaker: "The meeting is at 2pm... sorry, I meant 3pm."',
          options: ['3pm', '2pm', '2:30pm'],
          answer: '3pm',
          explanation: 'The corrected information (3pm) is the answer.',
        },
        {
          text: 'Speaker: "You can find us at 47 Oak Street... no wait, that\'s 74 Oak Street."',
          options: ['74', '47', '47 or 74'],
          answer: '74',
          explanation: 'The final correction is always the correct answer.',
        },
        {
          text: 'Speaker A: "The price is $15."\nSpeaker B: "Actually, it went up to $18."',
          options: ['$18', '$15', '$33'],
          answer: '$18',
          explanation: 'The updated/corrected price is the answer.',
        },
      ],
    },
    {
      title: 'Time Expressions',
      type: 'fill-in',
      instructions: 'Write the time in digits.',
      questions: [
        {
          text: 'You hear: "quarter to ten"',
          answer: '9:45',
          explanation: 'Quarter to = 15 minutes before the hour.',
        },
        {
          text: 'You hear: "half past seven"',
          answer: '7:30',
          explanation: 'Half past = 30 minutes after the hour.',
        },
        {
          text: 'You hear: "ten to eight"',
          answer: '7:50',
          explanation: 'Ten to = 10 minutes before the hour.',
        },
      ],
    },
    {
      title: 'Spelling Practice',
      type: 'select',
      instructions: 'Choose the correct spelling based on what you hear.',
      questions: [
        {
          text: 'You hear: "My name is Smith, that\'s S-M-I-T-H."',
          options: ['Smith', 'Smyth', 'Smiith'],
          answer: 'Smith',
          explanation: 'Write exactly as spelled out.',
        },
        {
          text: 'You hear: "That\'s Thompson with a P."',
          options: ['Thompson', 'Thomson', 'Tomson'],
          answer: 'Thompson',
          explanation: 'The clarification "with a P" confirms the spelling.',
        },
        {
          text: 'You hear: "It\'s McPherson, that\'s M-C, capital P-H-E-R-S-O-N."',
          options: ['McPherson', 'MacPherson', 'Mcpherson'],
          answer: 'McPherson',
          explanation: 'Capital letters were specified (Mc + capital P).',
        },
      ],
    },
  ],
  tips: [
    'Practice distinguishing similar-sounding numbers (13/30, 14/40)',
    'Learn common British number expressions (fortnight, dozen)',
    'Always double-check spelling during transfer time',
    'Listen for spelling clues: "with a double L"',
  ],
  dailyPractice: [
    'Practice writing down phone numbers dictated quickly',
    'Listen for and transcribe dates, times, and prices',
    'Practice spelling common English names',
    'Do number dictation exercises',
  ],
  backPath: '/exams/english/#listening',
  backLabel: 'Back to Listening',
  aiContext: 'Listening for Details: Catching specific information like numbers, names, dates, and addresses accurately, including awareness of common traps and corrections.',
};

// Difficulty-variant factory: returns content adapted to the given difficulty level.
// difficulty 1-2: simplified content with basic examples and easier exercises
// difficulty 3: base content (default)
// difficulty 4-5: advanced content with complex examples and harder exercises
export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...listeningDetailsData,
      title: listeningDetailsData.title + ' (Foundations)',
      overview: {
        ...listeningDetailsData.overview,
        duration: '15-20 minutes',
      },
      // Simplified: show only first 2 sections and first exercise set
      sections: listeningDetailsData.sections.slice(0, Math.max(2, Math.ceil(listeningDetailsData.sections.length / 2))),
      exercises: listeningDetailsData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...listeningDetailsData,
      title: listeningDetailsData.title + ' (Advanced)',
      overview: {
        ...listeningDetailsData.overview,
        duration: '30-35 minutes',
      },
      // Advanced: all sections plus advanced tips context
      sections: listeningDetailsData.sections,
      exercises: listeningDetailsData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(listeningDetailsData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS writing tasks.',
      ],
    };
  }
  return listeningDetailsData;
}
